import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";
import { playScanSound } from "./scanSound";

const MAX_RECENT = 50;
const storageKey = (scanType) => `warehouse_scan_session_${scanType}`;

function readStoredSessionId(scanType) {
  try {
    const value = sessionStorage.getItem(storageKey(scanType));
    return value ? Number(value) : null;
  } catch {
    return null;
  }
}

function storeSessionId(scanType, id) {
  try {
    if (id) sessionStorage.setItem(storageKey(scanType), String(id));
    else sessionStorage.removeItem(storageKey(scanType));
  } catch {
    // trình duyệt chặn storage — phiên chỉ mất khi F5
  }
}

/**
 * Phiên quét Kho Vận: mở/đóng phiên, hàng đợi mã (quét nhanh liên tiếp không mất mã,
 * gửi API tuần tự), thống kê real-time và danh sách kết quả gần nhất.
 * @param {"IN"|"OUT"} scanType
 * @param {{ autoOpen?: boolean }} options autoOpen: tự mở phiên khi chưa có (quét nhập)
 */
export default function useWarehouseScan(scanType, { autoOpen = false } = {}) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recent, setRecent] = useState([]);
  const [counts, setCounts] = useState({});
  const [lastResult, setLastResult] = useState(null);
  const [pending, setPending] = useState(0);
  const [pendingLogs, setPendingLogs] = useState([]);
  const [confirmedCodes, setConfirmedCodes] = useState([]);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [confirming, setConfirming] = useState(false);

  const sessionRef = useRef(null);
  const queueRef = useRef([]);
  const processingRef = useRef(false);

  const applySession = useCallback(
    (dto) => {
      sessionRef.current = dto;
      setSession(dto);
      storeSessionId(scanType, dto?.id);
      setCounts(dto?.counts || {});
      setRecent(dto?.recentLogs || []);
      setPendingLogs(dto?.pendingLogs || []);
      setConfirmedCodes(dto?.confirmedCodes || []);
      setConfirmedCount(dto?.confirmedCount || 0);
      setLastResult(null);
    },
    [scanType]
  );

  const openSession = useCallback(
    async (customerCode) => {
      try {
        const res = await axiosInstance.post("/api/warehouse/sessions", { scanType, customerCode });
        if (!res.data?.success) {
          toast.error(res.data?.message || "Không mở được phiên quét");
          return false;
        }
        applySession(res.data.data);
        return true;
      } catch (error) {
        toast.error(error.response?.data?.message || "Không mở được phiên quét");
        return false;
      }
    },
    [scanType, applySession]
  );

  const closeSession = useCallback(async () => {
    const current = sessionRef.current;
    queueRef.current = [];
    setPending(0);
    applySession(null);
    if (!current) return;
    try {
      await axiosInstance.put(`/api/warehouse/sessions/${current.id}/close`);
    } catch {
      // phiên sẽ không còn được dùng — bỏ qua lỗi đóng
    }
  }, [applySession]);

  // Khôi phục phiên đang mở sau khi F5; quét nhập tự mở phiên mới nếu chưa có.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const storedId = readStoredSessionId(scanType);
      let restored = false;
      if (storedId) {
        try {
          const res = await axiosInstance.get(`/api/warehouse/sessions/${storedId}`);
          const dto = res.data?.data;
          if (!cancelled && res.data?.success && dto?.status === "OPEN" && dto?.scanType === scanType) {
            applySession(dto);
            restored = true;
          }
        } catch {
          // phiên cũ không còn hợp lệ
        }
      }
      if (cancelled) return;
      if (!restored) {
        storeSessionId(scanType, null);
        if (autoOpen) await openSession();
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanType]);

  const pushResult = useCallback((dto) => {
    playScanSound(dto.sound);
    setLastResult({ ...dto, receivedAt: Date.now() });
    setRecent((prev) => [dto, ...prev].slice(0, MAX_RECENT));
    if (dto.result && dto.result !== "NETWORK") {
      setCounts((prev) => ({ ...prev, [dto.result]: (prev[dto.result] || 0) + 1 }));
    }
    if (dto.result === "READY") {
      setPendingLogs((prev) => [...prev, dto]);
    }
  }, []);

  /**
   * Xác nhận tất cả mã đang chờ — lúc này mới đổi trạng thái đơn.
   * payload (quét nhập): { assignments, createOrders } — gán KH cho đơn ZTO-VH00 / tạo đơn cho mã chưa tồn tại.
   */
  const confirmAll = useCallback(async (payload) => {
    const current = sessionRef.current;
    if (!current) return null;
    setConfirming(true);
    try {
      const res = await axiosInstance.post(`/api/warehouse/sessions/${current.id}/confirm`, payload);
      if (!res.data?.success) {
        toast.warning(res.data?.message || "Không xác nhận được");
        return null;
      }
      const data = res.data.data;
      const results = data?.results || [];
      const okCodes = results
        .filter((r) => r.result === "SUCCESS" || r.result === "UNCLAIMED_CREATED")
        .map((r) => r.code);
      setPendingLogs([]);
      setConfirmedCodes((prev) => [...prev, ...okCodes]);
      setConfirmedCount((prev) => prev + okCodes.length);
      setRecent((prev) => [...[...results].reverse(), ...prev].slice(0, MAX_RECENT));
      // Xác nhận không phát âm thanh — âm thanh chỉ dành cho lúc quét; kết quả báo bằng toast.
      if (data.failedCount > 0) toast.warning(res.data.message);
      else toast.success(res.data.message);
      return data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Lỗi kết nối — chưa xác nhận được, vui lòng thử lại");
      return null;
    } finally {
      setConfirming(false);
    }
  }, []);

  /** Bỏ một mã khỏi danh sách chờ (quét nhầm). */
  const removePending = useCallback(async (log) => {
    const current = sessionRef.current;
    if (!current || !log?.id) return;
    try {
      const res = await axiosInstance.delete(`/api/warehouse/sessions/${current.id}/pending/${log.id}`);
      if (!res.data?.success) {
        toast.warning(res.data?.message || "Không bỏ được mã");
        return;
      }
      setPendingLogs((prev) => prev.filter((item) => item.id !== log.id));
      setRecent((prev) => [res.data.data, ...prev].slice(0, MAX_RECENT));
    } catch (error) {
      toast.error(error.response?.data?.message || "Không bỏ được mã");
    }
  }, []);

  const processQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (queueRef.current.length > 0) {
        const code = queueRef.current.shift();
        setPending(queueRef.current.length);
        const current = sessionRef.current;
        if (!current) {
          playScanSound("ERROR");
          continue;
        }
        try {
          const url = scanType === "IN" ? "/api/warehouse/scanIn" : "/api/warehouse/scanOut";
          const res = await axiosInstance.post(url, { sessionId: current.id, code });
          const dto = res.data?.data;
          if (!dto) continue;
          pushResult(dto);
          if (dto.result === "NO_SESSION") {
            // Phiên đã bị đóng (tab khác / hết hạn): bắt mở phiên mới.
            queueRef.current = [];
            applySession(null);
            if (autoOpen) await openSession();
            break;
          }
        } catch (error) {
          const message =
            error.response?.status === 403
              ? "Bạn không có quyền quét"
              : "Lỗi kết nối — mã chưa được xử lý, vui lòng quét lại";
          pushResult({
            code,
            result: "NETWORK",
            sound: "ERROR",
            message,
            createdAt: new Date().toISOString(),
          });
        }
      }
    } finally {
      processingRef.current = false;
      setPending(queueRef.current.length);
    }
  }, [scanType, pushResult, applySession, autoOpen, openSession]);

  /** Nhận một hoặc nhiều mã (paste nhiều dòng) — mỗi mã kiểm tra lần lượt. */
  const enqueue = useCallback(
    (raw) => {
      // Tách theo khoảng trắng / xuống dòng / dấu phẩy, bỏ ký tự điều khiển máy quét gửi kèm.
      const codes = Array.from(String(raw || ""))
        .map((ch) => (ch.charCodeAt(0) <= 31 ? " " : ch))
        .join("")
        .split(/[\s,;]+/)
        .filter(Boolean);
      if (codes.length === 0) return;
      queueRef.current.push(...codes);
      setPending(queueRef.current.length);
      processQueue();
    },
    [processQueue]
  );

  const dismissLastResult = useCallback(() => setLastResult(null), []);

  const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
  const pendingCodes = useMemo(() => pendingLogs.map((log) => log.code), [pendingLogs]);

  return {
    session,
    loading,
    recent,
    counts,
    total,
    lastResult,
    pending,
    pendingLogs,
    pendingCodes,
    confirmedCodes,
    confirmedCount,
    confirming,
    openSession,
    closeSession,
    enqueue,
    confirmAll,
    removePending,
    dismissLastResult,
  };
}
